import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useNavigate, Link, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Row, Col } from 'react-bootstrap';
// icons
import { FiEdit } from 'react-icons/fi';
import { RiDeleteBinLine } from 'react-icons/ri';
//api
import { deleteTypeSchedule } from 'api/company';
import { getTypeSchedules } from 'api/pathForHook/company';
//utils
import useHandleSearch from 'utils/customHooks/useHandleSearch';
//components
import CustomTable from 'components/backoffice/customTable';
import Button from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import Title from 'components/backoffice/texts/title';
import { usePermissions } from 'components/backoffice/PermissionsContext';
import SearchTable from 'components/backoffice/table/SearchTable';

const TypeSchedules = () => {
  const userPermissions = usePermissions();
  const navigate = useNavigate();
  const [typeSchedules, setTypeSchedules] = useState([]);
  const [selectTypeSchedule, setSelectTypeSchedule] = useState({})
  const [page, setPage] = useState(1);
  const [inputValue, setInputValue] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [responseInfo, setResponseInfo] = useState({});
  const { t } = useTranslation(['backoffice']);
  const { setTitle } = useOutletContext()

  const colsTypeSchedules = [t('nameTypeSchedule'), ''];

  const { fetchData, stateData, loading } = useHandleSearch();

  const getTypeSchedulesList = async () => {
    try {
      let payload = {
        page,
        page_size: 10,
        search: inputValue || '',
        fields: 'id,name'
      }
      await fetchData(getTypeSchedules(), payload);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setTitle(t('typeSchedules'))
    return () => {
      setTitle('');
    };
  }, [setTitle])

  useEffect(() => {
    const { results, errors, totalPages, noResults } = stateData;
    if (results !== null) {
      setTypeSchedules(results);
      setResponseInfo({ errors, totalPages, noResults });
    } else {
      setTypeSchedules(null);
      setResponseInfo({ errors });
    }
  }, [stateData])

  useEffect(() => {
    if (page > 1 && inputValue !== '') {
      setPage(1);
    }
  }, [inputValue])

  useEffect(() => {
    getTypeSchedulesList();
  }, [page, inputValue]);

  const handleCreate = () => {
    navigate('/intranet/type-schedules/create');
  };

  const onDelete = async () => {
    const { status } = await deleteTypeSchedule(selectTypeSchedule.id);
    if (status === 204) {
      toast.success(t('eliminadocorrectamente'));
      getTypeSchedulesList();
      setShowDeleteModal(false);
    } else {
      toast(`${t('error')} ${status}: ${t('mensajeerror')}`);
    }
  };

  const openModalDelete = (object) => {
    setShowDeleteModal(true);
    setSelectTypeSchedule(object);
  };

  const handleClear = () => {
    setInputValue('');
    setPage(1);
    setResponseInfo(prevResponseInfo => ({ ...prevResponseInfo, noResults: false }));
    getTypeSchedulesList(null);
  };

  return (
    <div className="d-lg-grid">
      <Title text={t('typeSchedules')} />
      <Row className="d-flex w-md-100 mt-3">
        <Col md={5} xs={12} className="d-flex text-start">
          <SearchTable
            value={inputValue}
            handleSearch={setInputValue}
            handleClear={handleClear}
            handleKeyDown={true}
            placeholder={t('searchbyname')}
          />
        </Col>
        <Col md={7} xs={12} className="text-end mt-3 mt-md-0">
          {userPermissions.includes('typeschedule.__add') && (
            <Button color={'primary'} onClick={handleCreate}>
              {t('crear')}
            </Button>
          )}
        </Col>
      </Row>
      <div className="table-responsive">
        <CustomTable
          cols={colsTypeSchedules}
          noResults={responseInfo?.noResults}
          isLoading={loading}
          page={page}
          changePage={setPage}
          totalPages={responseInfo?.totalPages}
        >
          {typeSchedules &&
            typeSchedules.map((typeSchedule) => {
              return (
                <tr key={typeSchedule.id}>
                  <td>{typeSchedule.name}</td>
                  <td className="text-end">
                    {userPermissions.includes('typeschedule.__modify') && (
                      <Link to={`/intranet/type-schedules/edit/${typeSchedule.id}`}>
                        <FiEdit className="mx-3" color="white" size={20} />
                      </Link>
                    )}
                    {userPermissions.includes('typeschedule.__delete') && (
                      <RiDeleteBinLine
                        className="mx-3 style-icon-delete"
                        size={20}
                        onClick={() => openModalDelete(typeSchedule)}
                      />
                    )}
                  </td>
                </tr>
              );
            })}
        </CustomTable>
      </div>
      <DeleteModalCustom
        name={selectTypeSchedule?.name}
        openModal={showDeleteModal}
        setOpenModal={setShowDeleteModal}
        handleDelete={onDelete}
        deleteTitle={t('deletetypeschedule')}
        beforeTitle={t('typeSchedules')}
      />
    </div>
  );
};

export default TypeSchedules